import React from "react";
import { Box, Typography } from "../../../design-system";
import UploadDownloadArea from "./UploadDownloadArea";
import HeroClipDemo from "./HeroClipDemo";
import logoSite from "../../../../assets/brand/logo_site.svg";
// import TextType from "./TextType"; // typewriter intro — parked for now, keep for later

const MAIN_CONTENT_GAP = 48;
const MAIN_CONTENT_MAX_WIDTH = 1512;

// Short, scannable steps shown under the upload box.
const FEATURE_STEPS = [
  "Select clips in any order, then export",
  "Search any word and export it as a supercut",
  "Filter by speaker, emotion, time & more",
];

/**
 * Main content section: bold hero message + tagline + UploadDownloadArea.
 */
const MainSection: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: { xs: "24px", md: `${MAIN_CONTENT_GAP}px` },
      maxWidth: `${MAIN_CONTENT_MAX_WIDTH}px`,
      width: "100%",
      margin: "0 auto",
      padding: { xs: "16px 0", md: "32px 0" },
    }}
  >
    {/* ssemble-style hero: logo, one bold promise, second line in pink, then a quiet tagline. */}
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: { xs: "10px", md: "14px" } }}>
      <img
        src={logoSite}
        alt="CherryClipz"
        style={{ height: "auto", display: "block" }}
        width={56}
      />
      <Typography
        sx={{
          fontFamily: "Outfit, sans-serif",
          fontWeight: 800,
          textAlign: "center",
          lineHeight: 1.08,
          color: "#f3f4f9",
          fontSize: { xs: "32px", md: "56px" },
          letterSpacing: "-0.02em",
        }}
      >
        Every moment becomes a clip.
        <Box component="span" sx={{ display: "block", color: "#e61d50" }}>
          Cherry-pick the ones you want.
        </Box>
      </Typography>
      <Typography
        sx={{
          fontFamily: "Outfit, sans-serif",
          fontWeight: 500,
          textAlign: "center",
          color: "#e4e7f0",
          fontSize: { xs: "16px", md: "21px" },
          lineHeight: 1.4,
          maxWidth: "720px",
        }}
      >
        Upload a video or audio file. We cut every quote and silence into its own
        clip, ready to play, download, and share in just one click!
      </Typography>
    </Box>
    <UploadDownloadArea />
    {/* Honest expectation-setting — kept separate from the feature list. */}
    <Typography
      sx={{
        fontFamily: "Outfit, sans-serif",
        fontWeight: 500,
        textAlign: "center",
        color: "#8d94a8",
        fontSize: { xs: "12px", md: "13px" },
        mt: { xs: "-12px", md: "-28px" },
      }}
    >
      Longer videos take a little longer to process.
    </Typography>
    {/* Instructions strip — kept ABOVE the demo so the demo expanding/collapsing on
        loop never pushes this text around. */}
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        gap: { xs: "10px 18px", md: "12px 28px" },
        mt: { xs: "-4px", md: "-8px" },
      }}
    >
      {FEATURE_STEPS.map((step) => (
        <Box key={step} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box
            component="span"
            sx={{
              width: "6px", height: "6px", borderRadius: "50%",
              backgroundColor: "#e61d50", flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontFamily: "Outfit, sans-serif",
              fontWeight: 500,
              color: "#c2c7d6",
              fontSize: { xs: "13px", md: "16px" },
              whiteSpace: "nowrap",
            }}
          >
            {step}
          </Typography>
        </Box>
      ))}
    </Box>
    {/* Looping demo: upload box → process → clips generate → select → export. */}
    <HeroClipDemo />
  </Box>
);

export default MainSection;
